import type { ReactNode } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SparkleDecor } from "@/components/sparkle/SparkleDecor";

export function Layout() {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-muted/40 md:grid md:grid-cols-[240px_1fr]">
      <SparkleDecor />
      {/* Top bar (mobile) / sidebar (md+) */}
      <aside
        className={cn(
          "bg-card border-b md:border-b-0 md:border-r",
          "md:sticky md:top-0 md:h-screen md:overflow-y-auto",
          "md:p-4 md:flex md:flex-col",
        )}
      >
        {/* Mobile top-bar row: brand · nav · sign out */}
        <div className="flex items-center gap-3 p-3 md:hidden">
          <Link to="/" className="shrink-0 min-w-0">
            <div className="text-sm font-bold tracking-tight leading-tight truncate">
              {user?.household_name || "Today"}
            </div>
            <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground">
              Today
            </div>
          </Link>
          <nav className="flex gap-1 ml-auto overflow-x-auto">
            <MobileNavItem to="/">Planner</MobileNavItem>
            <MobileNavItem to="/channels">Integrations</MobileNavItem>
            <MobileNavItem to="/reports">Reports</MobileNavItem>
            <MobileNavItem to="/study">Study</MobileNavItem>
            <MobileNavItem to="/settings">Settings</MobileNavItem>
          </nav>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs shrink-0"
            onClick={() => void logout()}
          >
            Sign out
          </Button>
        </div>

        {/* Desktop sidebar */}
        <div className="hidden md:flex md:flex-col md:flex-1">
          <div className="px-2 pb-4 border-b">
            <Link to="/" className="block">
              <div className="text-base font-bold tracking-tight truncate">
                {user?.household_name || "Today"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-1">
                Today
              </div>
            </Link>
          </div>

          <nav className="mt-4 space-y-1 flex-1">
            <NavGroup label="Plan">
              <NavItem to="/">Planner</NavItem>
              <NavItem to="/channels">Integrations</NavItem>
              <NavItem to="/reports">Reports</NavItem>
              <NavItem to="/study">Study</NavItem>
            </NavGroup>
            <NavGroup label="You">
              <NavItem to="/settings">Settings</NavItem>
            </NavGroup>
          </nav>

          <div className="border-t pt-3 mt-3 space-y-2">
            <div className="px-2 space-y-0.5 min-w-0">
              <div className="text-xs font-semibold text-foreground truncate">
                {user?.display_name}
              </div>
              <div className="text-[11px] text-muted-foreground truncate" title={user?.email}>
                {user?.email}
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <main className="p-4 sm:p-6 md:p-10 max-w-[1200px] w-full mx-auto md:mx-0">
        <Outlet />
      </main>
    </div>
  );
}

function NavGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="pt-2">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold px-2 pb-2">
        {label}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function NavItem({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        cn(
          "block px-3 py-2 rounded-md text-sm font-medium transition-colors",
          isActive
            ? "bg-foreground text-background"
            : "text-foreground hover:bg-muted",
        )
      }
    >
      {children}
    </NavLink>
  );
}

function MobileNavItem({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      className={({ isActive }) =>
        cn(
          "px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
          isActive
            ? "bg-foreground text-background"
            : "text-foreground hover:bg-muted",
        )
      }
    >
      {children}
    </NavLink>
  );
}
